import { Button, Separator, Tooltip } from "@forge/ui";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { ListTree, LogOut, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { loadSession, meQueryOptions } from "../lib/auth";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

export const Route = createFileRoute("/_admin")({
  beforeLoad: async ({ context }) => {
    try {
      const me = await loadSession(context.queryClient);
      if (!("user" in me) || !me.user?.isSuperadmin) {
        throw new Error("not superadmin");
      }
      if ("twoFactorEnabled" in me && me.twoFactorEnabled === false) {
        throw redirect({ to: "/login" });
      }
    } catch {
      throw redirect({ to: "/login" });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const { data: me } = useSuspenseQuery(meQueryOptions);
  const router = useRouter();
  const queryClient = useQueryClient();
  const wideLayout = useRouterState({
    select: (s) => s.matches.at(-1)?.staticData?.layout === "wide",
  });
  const shellWidth = wideLayout ? "max-w-[100rem]" : "max-w-6xl";

  async function logout() {
    await api.auth.logout.post();
    queryClient.clear();
    await router.navigate({ to: "/login" });
  }

  return (
    <div className="min-h-dvh bg-surface">
      <header className="sticky top-0 z-40 border-b border-line bg-surface-raised">
        <div
          className={`mx-auto flex ${shellWidth} items-center justify-between px-6 py-2.5`}
        >
          <Link
            to="/"
            className="flex items-center gap-2 transition-transform duration-150 ease-swift active:scale-[0.97]"
          >
            <div className="flex size-6 items-center justify-center rounded-control bg-ink text-ink-inverse">
              <ShieldCheck className="size-3.5" />
            </div>
            <span className="text-[0.8125rem] font-semibold tracking-tight text-ink">
              Bridge Admin
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-2xs text-ink-muted">{me.user?.email}</span>
            <Separator orientation="vertical" className="h-4" />
            <Tooltip content="Painel da fila">
              <a
                href={`${API_URL}/admin/queues`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex size-8 items-center justify-center rounded-control text-ink-secondary hover:bg-surface-sunken hover:text-ink"
              >
                <ListTree className="size-4" />
                <span className="sr-only">Fila</span>
              </a>
            </Tooltip>
            <Tooltip content="Sair">
              <Button variant="ghost" size="icon" onClick={logout}>
                <LogOut />
              </Button>
            </Tooltip>
          </div>
        </div>
      </header>
      <main className={`mx-auto ${shellWidth} p-6`}>
        <Outlet />
      </main>
    </div>
  );
}
